import { useEffect, useRef } from 'react';
import { impersonationUtils } from '@utils/impersonation';
import { useOptionalTour } from './TourProvider';
import { tourForPebTab, tourForPibTab } from './tour-registry';
import { getTourUserId, hasSeenTour, markTourSeen } from './tour-storage';
import type { TourDefinition } from './types';

/** Tabs animate their content in; give it a beat before measuring the anchor. */
const SUB_TOUR_DELAY_MS = 600;

/**
 * Runs a tour bound to in-page state rather than a route — the open PIB tab.
 * Registers it with the provider too, so the "?" pill can offer it for replay.
 *
 * `ready` gates auto-start on the surface actually being usable (data loaded,
 * not mid-save). A sub-tour never interrupts a tour already on screen; it waits
 * for the running one to finish and then takes its turn.
 */
export const useSubTour = (tour: TourDefinition | undefined, label: string | undefined, ready: boolean) => {
  const ctx = useOptionalTour();
  const registerSubTour = ctx?.registerSubTour;
  const startTour = ctx?.startTour;
  const isRunning = ctx?.isRunning ?? false;

  useEffect(() => {
    registerSubTour?.(tour, label);
    return () => registerSubTour?.(undefined, undefined);
  }, [registerSubTour, tour, label]);

  const isTourRunning = ctx?.isTourRunning;

  useEffect(() => {
    if (!ready || !tour || !startTour || isRunning || impersonationUtils.isImpersonating()) {
      return;
    }
    const userId = getTourUserId();
    if (!userId || hasSeenTour(userId, tour.id)) {
      return;
    }
    const timer = window.setTimeout(() => {
      // Re-check at fire time, not just when the effect ran. The page tour
      // starts on its own timer, and `isRunning` is React state — a render
      // behind — so a sub-tour could slip past the guard and put a second
      // popover on screen next to it.
      if (isTourRunning?.()) {
        return;
      }
      markTourSeen(userId, tour.id);
      startTour(tour);
    }, SUB_TOUR_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [ready, tour, startTour, isRunning, isTourRunning]);
};

/**
 * The PIB form's per-tab tours.
 *
 * Deliberately suppressed for the tab that happens to be open when the page
 * first loads *if* the page's own overview tour hasn't been seen yet: a
 * first-time user should get the overview, and being handed a second tour the
 * moment they dismiss it reads as nagging. Once they actively switch tabs — or
 * on any later visit — every tab teaches itself on first open.
 */
export const usePibTabTour = (activeTab: string, ready: boolean) => {
  const initialTabRef = useRef(activeTab);
  const overviewSeenOnMountRef = useRef<boolean | null>(null);

  if (overviewSeenOnMountRef.current === null) {
    const userId = getTourUserId();
    overviewSeenOnMountRef.current = userId ? hasSeenTour(userId, 'pib-edit-overview') : false;
  }

  const isUntouchedInitialTab = activeTab === initialTabRef.current && !overviewSeenOnMountRef.current;
  const tour = tourForPibTab(activeTab);

  useSubTour(tour, PIB_TAB_LABELS[activeTab] ?? activeTab, ready && !isUntouchedInitialTab);
};

/** Tab labels as the form itself shows them — used in the "?" pill's menu. */
const PIB_TAB_LABELS: Record<string, string> = {
  header: 'Header',
  entitas: 'Entitas',
  dokumen: 'Dokumen',
  pengangkut: 'Pengangkut',
  kemasan: 'Kemasan & Peti Kemas',
  transaksi: 'Transaksi',
  barang: 'Barang',
  pungutan: 'Pungutan',
  pernyataan: 'Pernyataan',
};

/** Tab labels as the PEB form shows them, for the "?" menu. */
const PEB_TAB_LABELS: Record<string, string> = {
  header: 'Header',
  entitas: 'Entitas',
  document: 'Document',
  pengangkut: 'Pengangkut',
  kemasan: 'Kemasan & Peti Kemas',
  transaksi: 'Transaksi',
  barang: 'Barang',
  pernyataan: 'Pernyataan',
};

/**
 * The PEB form's per-tab tours. Same shape as the PIB one, including the
 * suppression of the initial tab's tour on a first visit so the page overview
 * gets to run on its own.
 */
export const usePebTabTour = (activeTab: string, ready: boolean) => {
  const initialTabRef = useRef(activeTab);
  const overviewSeenOnMountRef = useRef<boolean | null>(null);

  if (overviewSeenOnMountRef.current === null) {
    const userId = getTourUserId();
    overviewSeenOnMountRef.current = userId ? hasSeenTour(userId, 'export-form') : false;
  }

  const isUntouchedInitialTab = activeTab === initialTabRef.current && !overviewSeenOnMountRef.current;

  useSubTour(tourForPebTab(activeTab), PEB_TAB_LABELS[activeTab] ?? activeTab, ready && !isUntouchedInitialTab);
};
